import React, { FC, useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Table } from '@wmgtech/legato'
import { ColumnsType } from '@wmgtech/legato/build/components/Table/types'

import { ChartSourcesTableProps, ChartSourcesTableData } from './ChartSourcesTable.props'
import { EditableCell } from './EditableCell'
import { ActionButtons } from './ActionsButton'
import { useChartSourceUpdate } from 'modules/ChartSources/serviceHooks/useChartSourceUpdate'
import styles from './ChartSourcesTable.module.scss'

interface EditableColumn<RecordType> extends ColumnsType<RecordType> {
  editable?: boolean
}

export const ChartSourcesTable: FC<ChartSourcesTableProps> = ({ chartSourcesList, refetch }) => {
  const [editedKey, setEditingKey] = useState<number | null>(null)
  const [defaultValueState, updateDefaultState] = useState<any>(null)

  const { updateChartSourceItem } = useChartSourceUpdate()

  const formMethods = useForm()

  const columns: EditableColumn<ChartSourcesTableData>[] = [
    {
      dataIndex: 'chart',
      key: 'chart',
      title: 'Chart',
      sortable: false
    },
    {
      dataIndex: 'chart_id',
      key: 'chart_id',
      title: 'Chart ID',
      sortable: false,
      editable: true
    },
    {
      dataIndex: 'configuration',
      key: 'configuration',
      title: 'Configuration',
      sortable: false
    },
    {
      dataIndex: 'country_code',
      key: 'country_code',
      title: 'Country Code',
      sortable: false
    },
    {
      dataIndex: 'cycle',
      key: 'cycle',
      title: 'Cycle',
      sortable: false
    },
    {
      dataIndex: 'import_limit',
      key: 'import_limit',
      title: 'Import Limit',
      sortable: false
    },
    {
      dataIndex: 'release_info',
      key: 'release_info',
      title: 'Release Info',
      sortable: false
    },
    {
      key: 'edit',
      title: 'Edit',
      sortable: false,
      align: 'right',
      width: '5%',
      render: (value: string, source: ChartSourcesTableData) => {
        const isEdited = source.id === editedKey
        const isNotOfficial = !source.chart.startsWith('official')
        return (
          isNotOfficial && (
            <ActionButtons
              isEditing={isEdited}
              onEdit={() => changeEditableRow(source.id)}
              onCancel={() => resetRowState()}
              onSave={() => updateChartSource(source.chart)}
              isDisabled={editedKey !== null}
            />
          )
        )
      }
    }
  ]

  const changeEditableRow = useCallback(
    (key: number) => {
      const targetRow = chartSourcesList?.find((item, index) => index === key)
      updateDefaultState({ ...targetRow })
      setEditingKey(key)
    },
    [chartSourcesList]
  )

  const resetRowState = useCallback(() => {
    formMethods.unregister()
    updateDefaultState(null)
    setEditingKey(null)
  }, [formMethods])

  const updateChartSource = useCallback(
    (chartName: string) => {
      const chartId = formMethods.getValues()
      const chart = { chart: chartName }

      updateChartSourceItem({ variables: { input: { ...chartId, ...chart } } }).then(() => {
        refetch()
        resetRowState()
      })
    },
    [formMethods, refetch, resetRowState, updateChartSourceItem]
  )

  const mergedColumns: EditableColumn<ChartSourcesTableData>[] = columns.map((col) => {
    if (!col.editable) return col
    return {
      ...col,
      onCell: (record: ChartSourcesTableData) => ({
        title: col.title as string,
        editing: record.id === editedKey,
        dataIndex: col.dataIndex,
        defaultValueState
      })
    }
  })

  return (
    <FormProvider {...formMethods}>
      <Table
        className={styles['chart-sources-table']}
        columns={mergedColumns}
        tableLayout="fixed"
        data={chartSourcesList}
        rowKey={(record) => record.id}
        pagination={false}
        components={{
          body: {
            cell: EditableCell
          }
        }}
      />
    </FormProvider>
  )
}
